import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Chip,
    Drawer,
    FormControlLabel,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Stack,
    Switch,
    Toolbar,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import cx from 'classnames';
import { Close, DarkMode, LightMode, Menu as MenuIcon, Search } from '@mui/icons-material';
import { initials, userName } from 'c-admin/Lib';
import { useDispatch, useSelector } from 'react-redux';
import { useCommonTranslation } from 'c-translation';
import { Avatar, Button, IconTopBarBrandLogo, MuiRouterLink, TextField } from 'c-components';
import { PostAuthRoutes } from 'c-routes';
import { useLogout } from 'c-auth-module/Hooks';
import { usePostAuthHome } from 'c-wrapper/Components/CommonWrapper';
import { useAuthenticatedUser, useThemeMode, useUserImpersonation } from 'c-hooks';
import { useAtom } from 'jotai';
import { atom_useLegacyDashboard, atom_useLegacySummary } from 'c-wrapper/Atoms/legacyViewAtoms';
import { useHistory } from 'react-router-dom';
import { isRunningOnMac, isRunningStorybook, isRunningTests } from 'c-is-running';
import { authSelectors } from 'c-auth-module/Slices';
import ContentFooter from 'c-wrapper/Components/ContentFooter';
import type { DrawerConfiguration, DrawerNavigationItem } from 'c-wrapper';
import DrawerNavigationGroup from 'c-wrapper/Components/DrawerNavigationGroup/DrawerNavigationGroup';
import config from 'c-config';
import { NetworkRequestState } from 'c-data-layer';
import TopBarSystemSearch from './TopBarSystemSearch';
import { systemSearchSelectors, systemSearchThunks } from '../../../Modules/SystemSearch';

const useStyles = makeStyles(theme => ({
    menuButton: {
        marginRight: theme.spacing(5.5),
    },
    hide: {
        display: 'none',
    },
    avatar: {
        width: theme.spacing(5),
        height: theme.spacing(5),
        cursor: 'pointer',
    },
    absoluteDrawerIcon: {
        position: 'absolute',
    },
    invisibleSystemSearch: {
        visibility: 'hidden',
    },
    mobileMenuButton: {
        display: 'flex',
        [theme.breakpoints.up('md')]: {
            display: 'none',
        },
    },
    desktopNavigation: {
        display: 'flex',
        alignItems: 'stretch',
        alignSelf: 'stretch',
        height: theme.spacing(8),
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
    mobileDrawer: {
        width: theme.spacing(44),
        '& .MuiDrawer-paper': {
            width: theme.spacing(44),
            boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)',
        },
    },
    searchField: {
        [theme.breakpoints.down('sm')]: {
            display: 'none',
        },
    },
}));

type Props = {
    mobileDrawer?: boolean;
    navConfig?: DrawerConfiguration;
};

// Every item with a link, nested items included, keyed by link (later entries win, as before).
const linkMapOf = (
    items: { [key: number]: DrawerNavigationItem } = {},
): Record<string, DrawerNavigationItem> =>
    Object.values(items).reduce<Record<string, DrawerNavigationItem>>(
        (acc, item) => ({
            ...acc,
            ...(item.link ? { [item.link]: item } : {}),
            ...(item.items ? linkMapOf(item.items) : {}),
        }),
        {},
    );

const withFavouritesAtEnd = (
    groups: DrawerConfiguration['groups'],
    favourites: string[] = [],
): DrawerConfiguration['groups'] => {
    if (!Array.isArray(favourites) || favourites.length === 0) {
        return groups;
    }

    const linkMap = Object.values(groups).reduce<Record<string, DrawerNavigationItem>>(
        (acc, group) => ({ ...acc, ...linkMapOf(group.items) }),
        {},
    );

    const uniqueFavouriteLinks = [...new Set(favourites)].filter(Boolean);
    const favouriteItems = uniqueFavouriteLinks
        .map(link => linkMap[link])
        .filter(Boolean) as DrawerNavigationItem[];

    if (favouriteItems.length === 0) {
        return groups;
    }

    const nextGroups = Object.entries(groups).reduce(
        (acc, [groupKey, group]) => ({
            ...acc,
            [+groupKey]: {
                ...group,
                items: { ...(group.items ?? {}) },
            },
        }),
        {} as DrawerConfiguration['groups'],
    );
    const nextGroupIndex = Math.max(0, ...Object.keys(nextGroups).map(Number)) + 1;

    nextGroups[nextGroupIndex] = {
        items: favouriteItems.reduce(
            (acc, item, index) => ({
                ...acc,
                [index + 1]: item,
            }),
            {} as { [key: number]: DrawerNavigationItem },
        ),
    };

    return nextGroups;
};

const AppBarToolbar: React.FC<Props> = ({
    mobileDrawer = false,
    navConfig = config.navigation,
}) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const $user = useSelector(authSelectors.authenticatedUser);
    const isSystemSearchOpen = useSelector(systemSearchSelectors.isOpen);
    const searchTerm = useSelector(systemSearchSelectors.searchTerm);
    const defaultSearchPlaceholder = useCommonTranslation('SystemSearch.defaultSearchPlaceholder');
    const { logout } = useLogout();
    const postAuthHome = usePostAuthHome();
    const { stop, isImpersonating, stopImpersonatingLoadingState } = useUserImpersonation();
    const { isAdmin } = useAuthenticatedUser();
    const [useLegacySummary, setUseLegacySummary] = useAtom(atom_useLegacySummary);
    const [useLegacyDashboard, setUseLegacyDashboard] = useAtom(atom_useLegacyDashboard);
    const { isDarkMode, setMode, toggleMode } = useThemeMode();

    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const inputRef = useRef<HTMLInputElement>();

    const userNameString = useMemo(() => userName($user), [$user]);
    const userInitials = useMemo(() => initials(userNameString).substr(0, 2), [userNameString]);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

    const handleClick = useCallback(
        (event: React.MouseEvent<HTMLElement>) => {
            setAnchorEl(event.currentTarget);
        },
        [setAnchorEl],
    );

    const handleClose = useCallback(() => {
        setAnchorEl(null);
    }, [setAnchorEl]);

    const signOut = useCallback(() => {
        setAnchorEl(null);
        logout();
    }, [setAnchorEl, logout]);

    const myProfileClick = useCallback(() => {
        history.push(PostAuthRoutes.Users.MyProfile);
    }, [history]);

    const openSystemSearch = useCallback(() => {
        if (!isSystemSearchOpen) {
            if (inputRef?.current) {
                inputRef.current?.blur();
            }
            dispatch(systemSearchThunks.open());
        }
    }, [dispatch, isSystemSearchOpen, inputRef]);

    const closeSystemSearch = useCallback(() => {
        if (isSystemSearchOpen) {
            dispatch(systemSearchThunks.close());
        }
    }, [dispatch, isSystemSearchOpen]);
    const closeThen = useCallback(
        (afterClose: () => void) => () => {
            handleClose();
            afterClose();
        },
        [handleClose],
    );
    const t = useCommonTranslation();
    const displayGroups = useMemo(
        () => withFavouritesAtEnd(navConfig.groups, $user?.favourites ?? []),
        [navConfig.groups, $user?.favourites],
    );
    const sidebarStopWidth = useMemo(() => theme.spacing(29), [theme]);
    const defaultSidebarStop = useMemo(
        () => Math.round(parseFloat(String(sidebarStopWidth))),
        [sidebarStopWidth],
    );

    const toggleMobileMenu = useCallback(() => {
        setMobileMenuOpen(prev => !prev);
    }, []);

    const logoBlockRef = useRef<HTMLDivElement | null>(null);
    const navStartRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const compute = () => {
            const navLeft = navStartRef.current?.getBoundingClientRect().left;
            const logoRight = logoBlockRef.current?.getBoundingClientRect().right;

            // Prefer the actual nav start; otherwise use logo right; otherwise a sane default.
            const px = Math.round(navLeft ?? logoRight ?? defaultSidebarStop);
            document.documentElement.style.setProperty('--app-sidebar-stop', `${px}px`);
        };

        compute();

        const ro = new ResizeObserver(compute);
        if (logoBlockRef.current) ro.observe(logoBlockRef.current);
        if (navStartRef.current) ro.observe(navStartRef.current);

        const onResize = () => compute();
        window.addEventListener('resize', onResize, { passive: true });

        return () => {
            ro.disconnect();
            window.removeEventListener('resize', onResize);
            document.documentElement.style.setProperty(
                '--app-sidebar-stop',
                `${defaultSidebarStop}px`,
            );
        };
    }, [defaultSidebarStop]);

    return (
        <>
            <Toolbar
                disableGutters
                sx={{
                    position: 'sticky',
                    zIndex: 1000,
                    minHeight: theme.spacing(8),
                    height: theme.spacing(8),
                    '&.MuiToolbar-gutters': {
                        paddingLeft: theme.spacing(0.5),
                        paddingRight: 0,
                    },
                }}
            >
                <Box
                    display="flex"
                    alignItems={isMobile ? 'center' : 'stretch'}
                    width="100%"
                    height="100%"
                    pl={isMobile ? 0.5 : 2.5}
                >
                    {/* Mobile menu button */}
                    {isMobile && (
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            onClick={toggleMobileMenu}
                            className={classes.mobileMenuButton}
                            sx={{
                                mr: 2,
                                p: 0.5,
                                color: 'text.secondary',
                                backgroundColor: 'transparent',
                                border: 'none',
                                boxShadow: 'none',
                                '&:hover': {
                                    backgroundColor: 'transparent',
                                },
                            }}
                        >
                            <MenuIcon fontSize="inherit" />
                        </IconButton>
                    )}
                    {isMobile && (
                        <Box
                            ref={logoBlockRef}
                            sx={{
                                width: sidebarStopWidth,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                transform: 'translateY(2px)',
                            }}
                        >
                            <MuiRouterLink to={postAuthHome}>
                                <IconTopBarBrandLogo />
                            </MuiRouterLink>
                        </Box>
                    )}

                    {/* Desktop logo block */}
                    {!isMobile && (
                        <Box
                            pr={1}
                            ref={logoBlockRef}
                            sx={{
                                width: sidebarStopWidth,
                                justifyContent: 'center',
                                display: 'flex',
                                alignItems: 'center',
                                alignSelf: 'center',
                                ml: -0.75,
                                mt: 0.5,
                                transform: 'translateY(2px)',
                            }}
                        >
                            <MuiRouterLink to={postAuthHome}>
                                <IconTopBarBrandLogo />
                            </MuiRouterLink>
                        </Box>
                    )}
                    {/* Desktop Navigation */}
                    <Box
                        className={classes.desktopNavigation}
                        ref={navStartRef}
                        sx={{ height: '100%' }}
                    >
                        <MemoizedGroups groups={displayGroups} />
                    </Box>

                    {/* Right side items */}
                    <Box
                        display="flex"
                        alignItems="center"
                        alignSelf="center"
                        gap={isMobile ? 2 : 6}
                        ml="auto"
                        pr={3}
                    >
                        <Stack direction="row" spacing={2}>
                            <TextField
                                id="system-search"
                                variant="standard"
                                whiteBackdrop={false}
                                placeholder={
                                    searchTerm.length > 0 ? searchTerm : defaultSearchPlaceholder
                                }
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Chip
                                                label={isRunningOnMac ? '⌘K' : 'Ctrl+K'}
                                                size="small"
                                            />
                                        </InputAdornment>
                                    ),
                                }}
                                onFocus={openSystemSearch}
                                ref={inputRef}
                                className={cx(classes.searchField, {
                                    [classes.invisibleSystemSearch]: isSystemSearchOpen,
                                })}
                            />

                            <IconButton
                                onClick={!isSystemSearchOpen ? openSystemSearch : closeSystemSearch}
                            >
                                {!isSystemSearchOpen ? <Search /> : <Close />}
                            </IconButton>

                            <Tooltip
                                title={t(
                                    isDarkMode
                                        ? 'Navigation.switchToLightModeLabel'
                                        : 'Navigation.switchToDarkModeLabel',
                                )}
                            >
                                <IconButton id="toggle-dark-mode" onClick={toggleMode}>
                                    {isDarkMode ? <LightMode /> : <DarkMode />}
                                </IconButton>
                            </Tooltip>
                        </Stack>
                        {isImpersonating && (
                            <Button
                                variant="outlined"
                                onClick={stop}
                                size="small"
                                sx={{
                                    height: theme.spacing(5),
                                    textTransform: 'none',
                                    whiteSpace: 'nowrap',
                                }}
                                disabled={
                                    stopImpersonatingLoadingState.state ===
                                    NetworkRequestState.InProgress
                                }
                            >
                                {t('Navigation.adminStopImpersonateUser')}
                            </Button>
                        )}

                        <Avatar
                            id="user-menu"
                            className={cx([
                                classes.avatar,
                                { open: !!anchorEl, closed: !anchorEl },
                            ])}
                            onClick={handleClick}
                        >
                            <Typography variant="body1">{userInitials}</Typography>
                        </Avatar>
                    </Box>
                </Box>
            </Toolbar>

            {/* Left side navigation for desktop - REMOVED */}

            {/* Mobile Navigation Drawer */}
            <Drawer
                anchor="left"
                open={mobileMenuOpen}
                onClose={toggleMobileMenu}
                className={classes.mobileDrawer}
                classes={{
                    paper: classes.mobileDrawer,
                }}
            >
                <Box
                    sx={{
                        p: 2,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <MuiRouterLink to={postAuthHome} onClick={toggleMobileMenu}>
                        <IconTopBarBrandLogo />
                    </MuiRouterLink>
                    <IconButton onClick={toggleMobileMenu} size="small">
                        <Close />
                    </IconButton>
                </Box>
                <Box
                    sx={{
                        width: '100%',
                        mt: 1,
                        overflowY: 'auto',
                        maxHeight: `calc(100vh - ${theme.spacing(10)})`,
                    }}
                >
                    {Object.entries(displayGroups).map(([, value], index) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <Box key={index} sx={{ width: '100%', px: 2, py: 1 }}>
                            <DrawerNavigationGroup
                                items={Object.entries(value.items).map(x => x[1])}
                            />
                        </Box>
                    ))}
                </Box>
            </Drawer>

            <Menu
                anchorEl={anchorEl}
                keepMounted
                open={Boolean(anchorEl)}
                // getContentAnchorEl={null}
                onClose={handleClose}
                // https://app.clickup.com/t/nz80qw
                container={isRunningTests || isRunningStorybook ? () => anchorEl : undefined}
                anchorOrigin={{
                    horizontal: 'center',
                    vertical: 'bottom',
                }}
            >
                <MenuItem
                    onClick={closeThen(myProfileClick)}
                    className="my-profile"
                    sx={{ fontSize: '0.95rem' }}
                >
                    {useCommonTranslation('Pages.MyProfile.title')}
                </MenuItem>
                {!isImpersonating && (
                    <MenuItem
                        onClick={closeThen(signOut)}
                        className="sign-out"
                        sx={{ fontSize: '0.95rem' }}
                    >
                        {t('Navigation.logoutLabel')}
                    </MenuItem>
                )}
                <MenuItem
                    disableRipple
                    className="toggle-dark-mode"
                    sx={{ fontSize: '0.95rem' }}
                    onKeyDown={e => e.stopPropagation()}
                >
                    <FormControlLabel
                        control={
                            <Switch
                                size="small"
                                checked={isDarkMode}
                                onChange={(_, checked) => setMode(checked ? 'dark' : 'light')}
                            />
                        }
                        label={t('Navigation.darkModeLabel')}
                        sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                    />
                </MenuItem>
                {isAdmin && (
                    <MenuItem
                        disableRipple
                        className="toggle-legacy-summary"
                        sx={{ fontSize: '0.95rem' }}
                        onKeyDown={e => e.stopPropagation()}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={!useLegacySummary}
                                    onChange={(_, checked) => setUseLegacySummary(!checked)}
                                />
                            }
                            label="Summary 2.0"
                            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                        />
                    </MenuItem>
                )}
                {isAdmin && (
                    <MenuItem
                        disableRipple
                        className="toggle-legacy-dashboard"
                        sx={{ fontSize: '0.95rem' }}
                        onKeyDown={e => e.stopPropagation()}
                    >
                        <FormControlLabel
                            control={
                                <Switch
                                    size="small"
                                    checked={!useLegacyDashboard}
                                    onChange={(_, checked) => setUseLegacyDashboard(!checked)}
                                />
                            }
                            label="Dashboard 2.0"
                            sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: '0.95rem' } }}
                        />
                    </MenuItem>
                )}
                <MenuItem>
                    <ContentFooter />
                </MenuItem>
            </Menu>
            <TopBarSystemSearch />
        </>
    );
};

const MemoizedGroups = React.memo<DrawerConfiguration>(function NavigationGroups({ groups }) {
    return (
        <Box display="flex" gap={2.75} alignItems="stretch" height="100%">
            {Object.entries(groups).map(([, value], index) => (
                // eslint-disable-next-line react/no-array-index-key
                <Box key={index} height="100%" display="flex">
                    <DrawerNavigationGroup items={Object.entries(value.items).map(x => x[1])} />
                </Box>
            ))}
        </Box>
    );
});

export default AppBarToolbar;
